import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { ShieldAlert } from 'lucide-react'
import { changePassword, logout as logoutApi } from '@/api/services/auth.service'
import { Button, Card, Input } from '@/components/ui'
import { PageMetadata } from '@/components/common/PageMetadata'
import { brandAssets } from '@/config/brandAssets'
import { meQueryKey } from '@/features/settings/hooks/useMe'
import { PASSWORD_HINT, newPasswordSchema } from '@/lib/passwordPolicy'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { queryClient } from '@/main'
import { useAuthStore } from '@/stores/useAuthStore'

const forcedChangeSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter the temporary password you signed in with'),
    newPassword: newPasswordSchema,
    confirmNewPassword: z.string().min(1, 'Please confirm your new password'),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'New passwords do not match',
    path: ['confirmNewPassword'],
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: 'Choose a password different from the temporary one',
    path: ['newPassword'],
  })

type ForcedChangeValues = z.infer<typeof forcedChangeSchema>

/**
 * Shown instead of the app while the account must pick a new password, e.g.
 * after an admin set a temporary one. The server keeps this session and ends
 * the others, so on success the user goes straight into the app.
 */
export function ForcedPasswordChange(): React.ReactElement {
  const navigate = useNavigate()
  const email = useAuthStore((s) => s.user?.email)
  const setUser = useAuthStore((s) => s.setUser)
  const clearAuth = useAuthStore((s) => s.logout)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForcedChangeValues>({
    resolver: zodResolver(forcedChangeSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmNewPassword: '' },
  })

  const mutation = useMutation({
    mutationFn: (values: ForcedChangeValues) =>
      changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      }),
    onSuccess: () => {
      setUser((current) => (current ? { ...current, mustChangePassword: false } : current))
      void queryClient.invalidateQueries({ queryKey: meQueryKey })
    },
  })

  const signOut = (): void => {
    logoutApi().finally(() => {
      queryClient.clear()
      clearAuth()
      navigate('/login', { replace: true })
    })
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-gray-50 px-4 py-10 pt-[max(2.5rem,env(safe-area-inset-top))] pb-[max(2.5rem,env(safe-area-inset-bottom))] dark:bg-gray-950">
      <PageMetadata />
      <div className="w-full max-w-md space-y-6">
        <img
          src={brandAssets.logo}
          alt="KAAMCO"
          className="mx-auto h-auto w-36 object-contain dark:brightness-0 dark:invert"
        />
        <Card contentClassName="p-5 sm:p-8">
          <form onSubmit={handleSubmit((values) => mutation.mutate(values))} className="space-y-4">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Choose a new password
              </h1>
              {email ? (
                <p className="mt-1 truncate text-sm text-gray-500 dark:text-gray-400">{email}</p>
              ) : null}
            </div>

            <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <p>
                Your administrator gave you a temporary password. Choose your own to continue.
              </p>
            </div>

            {mutation.isError ? (
              <p className="text-sm text-danger" role="alert">
                {getErrorMessage(mutation.error)}
              </p>
            ) : null}

            <Input
              label="Temporary password"
              type="password"
              autoComplete="current-password"
              showPasswordToggle
              error={errors.currentPassword?.message}
              {...register('currentPassword')}
            />
            <Input
              label="New password"
              type="password"
              autoComplete="new-password"
              showPasswordToggle
              hint={PASSWORD_HINT}
              error={errors.newPassword?.message}
              {...register('newPassword')}
            />
            <Input
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              showPasswordToggle
              error={errors.confirmNewPassword?.message}
              {...register('confirmNewPassword')}
            />
            <Button type="submit" className="min-h-11 w-full" loading={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save and continue'}
            </Button>
          </form>
        </Card>
        <p className="text-center text-sm text-gray-500 dark:text-gray-400">
          Not you?{' '}
          <button
            type="button"
            onClick={signOut}
            className="font-medium text-primary hover:underline"
          >
            Sign out
          </button>
        </p>
      </div>
    </div>
  )
}

ForcedPasswordChange.displayName = 'ForcedPasswordChange'
